export const INDEX_URLS = [
  { url: 'https://ben-gy.github.io/gh-tool-factory/index/tools.json', key: 'tools' },
  { url: 'https://ben-gy.github.io/gh-site-factory/index/sites.json', key: 'sites' },
  { url: 'https://ben-gy.github.io/gh-game-factory/index/games.json', key: 'games' },
] as const;

/** Product slugs lab must never surface, regardless of what the factory indexes
 *  publish. The block is applied right after the indexes load (src/data/fetch.ts),
 *  so a listed product is absent from the grid, search, filters, trending,
 *  counts — and from the cached copy. The product still exists in its factory
 *  index; lab simply doesn't list it. To un-retire one, delete it from here.
 *
 *  Mirrored, by hand, in scripts/seo/lib.mjs (BLOCKED_SLUGS) so the build-time
 *  SEO prerender + sitemap exclude the same set; tests/blocklist-sync.test.ts
 *  asserts the two copies never drift.
 *
 *  - provenova          Provenova (tool-factory) — provenova.net; listed instead as
 *                       one of Ben's products (src/data/products.json)
 *  - benrichardson-dev  Ben Richardson OS (site-factory) — benrichardson.dev */
export const BLOCKED_SLUGS: readonly string[] = ['provenova', 'benrichardson-dev'];

export const ANALYTICS_URL = '/data/analytics.json';

/** In the Detail's published investigations index. Pulled live (like the factory
 *  indexes) to power the newsroom dateline + "Related reading" on data explorers.
 *  Adding a piece there — with its hubExplorers slugs — surfaces it here on the
 *  next load, with no lab deploy or code change. */
export const INVESTIGATIONS_URL = 'https://inthedetail.org/index/investigations.json';

/** Whether lab shows In the Detail at all (the dateline under the header and
 *  "Related reading" on data explorers). Off while inthedetail.org is offline
 *  behind its password (20 Oct 2026): lab then never asks for the index. Set
 *  true to bring both back. */
export const SHOW_IN_THE_DETAIL = false;

/** Fail the investigations fetch fast so a slow newsroom never delays lab. */
export const INVESTIGATIONS_TIMEOUT_MS = 6000;

/** Anonymous GitHub API: every ben-gy repo with its last-push time, newest
 *  first. 100 per page covers all repos, so page 1 always holds anything
 *  pushed recently — no pagination needed to find the last 24h. */
export const REPOS_URL =
  'https://api.github.com/users/ben-gy/repos?sort=pushed&direction=desc&per_page=100&type=owner';

/** Each factory's build logs (anonymous GitHub API, contents of logs/). Every
 *  factory run writes logs/<YYYY-MM-DD>-<slug>.md, shipped or not, and nothing
 *  else does: an entry added to an index by hand (Huttese) and Ben's products
 *  write none. So the newest log is the last time a factory ran. */
export const FACTORY_LOG_URLS = [
  'https://api.github.com/repos/ben-gy/gh-tool-factory/contents/logs',
  'https://api.github.com/repos/ben-gy/gh-site-factory/contents/logs',
  'https://api.github.com/repos/ben-gy/gh-game-factory/contents/logs',
] as const;

/** The experiment reads as paused once no factory has run for this many days
 *  (they run daily, so three quiet days isn't a missed run). */
export const PAUSE_AFTER_DAYS = 3;

export const FACTORY_RUNS_KEY = 'lab:factory-runs:v1';

/** The last factory run changes at most daily: keep it six hours, so a visitor
 *  makes three GitHub requests at most four times a day. */
export const FACTORY_RUNS_TTL_MS = 6 * 60 * 60 * 1000;

/** Entries shipped within this many hours get the ✨ NEW badge.
 *  Future-dated ships (factory clock/timezone skew) always count as new. */
export const NEW_HOURS = 24;

/** Entries whose repo was pushed within this many hours get the UPDATED badge
 *  (unless they're already NEW, which takes precedence). */
export const UPDATED_HOURS = 24;

/** Minimum 7-day visits before an entry can qualify as trending. */
export const TREND_MIN_V7 = 10;

/** Additive smoothing constant for the trend ratio. */
export const TREND_SMOOTH_K = 5;

/** Trend ratio at or above which the 🔥 badge shows. */
export const TREND_BADGE = 1.5;

export const CACHE_KEY = 'lab:cache:v1';
export const THEME_KEY = 'lab:theme';
export const PUSHES_KEY = 'lab:pushes:v2';

/** Web Push backend (the Cloudflare Worker in ./worker). This origin must also
 *  be allowed in the index.html CSP `connect-src`. Swap for your Worker's
 *  custom route, or its `<name>.<subdomain>.workers.dev` URL. */
export const PUSH_API_URL = 'https://push.benrichardson.dev';

/** VAPID application-server public key: base64url of the uncompressed P-256
 *  point (65 bytes, 0x04-prefixed). Public by design — it ships in the client
 *  and is handed to PushManager.subscribe as the applicationServerKey.
 *  Generate the pair with `node scripts/push/gen-vapid.mjs`; the matching
 *  private key lives only in the Worker's secrets. Empty ⇒ the notify UI stays
 *  hidden (nothing to subscribe to yet). */
export const VAPID_PUBLIC_KEY = 'BAMjJFec7dciqtluJkzB66cno8KeB__SerhO-GnydTz50n-SsCQbSVN1x99xf27Gt9yBZfZQiDHU1hw1k-zZKSE';

/** How long a cached repo-push map stays fresh (ms) — keeps rapid reloads off
 *  the anonymous GitHub rate limit while staying current enough for a 24h badge. */
export const PUSHES_TTL_MS = 15 * 60 * 1000;

export const SEARCH_DEBOUNCE_MS = 150;

export const SEARCH_PLACEHOLDERS = [
  "try 'pdf'",
  "try 'secret'",
  "try 'flights'",
  "try 'photo'",
  "try 'crime'",
  "try 'transcribe'",
];
